/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService, Product, Category } from '../services/api';
import { shopService, Shop, ShopProduct } from '../services/shopService';
import { getValidImageUrl } from '../utils/imageUtils';
import { useCategoryTranslation } from '../utils/categoryTranslations';
import CustomerHeader from '../components/CustomerHeader';
import ShopSelectionModal from '../components/ShopSelectionModal';
import { useCartCount } from '../hooks/useCartCount';
import './SearchPage.css';

type SearchTab = 'products' | 'shops' | 'categories' | 'brands';

interface BrandResult {
  name: string;
  productCount?: number;
}

const SearchPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const translateCategoryName = useCategoryTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<SearchTab>('products');
  const [loading, setLoading] = useState(false);
  const [cartItems, setCartItems] = useState<Array<{ product: ShopProduct; shop: Shop; quantity: number }>>([]);
  
  // Use shared hook for cart count
  const cartCount = useCartCount(500);

  // Search results for each tab
  const [productResults, setProductResults] = useState<Product[]>([]);
  const [shopResults, setShopResults] = useState<Shop[]>([]);
  const [categoryResults, setCategoryResults] = useState<Category[]>([]);
  const [brandResults, setBrandResults] = useState<BrandResult[]>([]);

  // Shop selection modal state
  const [showShopModal, setShowShopModal] = useState(false);
  const [selectedProductForCart, setSelectedProductForCart] = useState<Product | null>(null);

  // Get search query from URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q') || '';
    if (query) {
      setSearchQuery(query);
      handleSearch(query);
    }
  }, [location.search]);

  // Load cart items
  useEffect(() => {
    try {
      const storedCart = localStorage.getItem('multiShopCart');
      if (storedCart) {
        const parsedCart = JSON.parse(storedCart);
        setCartItems(parsedCart);
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    }
  }, []);

  // Save cart items
  useEffect(() => {
    try {
      localStorage.setItem('multiShopCart', JSON.stringify(cartItems));
      // Dispatch cart update event to update header
      window.dispatchEvent(new CustomEvent('cartUpdated'));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [cartItems]);

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    
    if (query.length < 2) {
      // Clear all results
      setProductResults([]);
      setShopResults([]);
      setCategoryResults([]);
      setBrandResults([]);
      return;
    }

    try {
      setLoading(true);
      
      // Search all types in parallel (matching mobile app logic)
      await Promise.all([
        searchProducts(query),
        searchShops(query),
        searchCategories(query),
        searchBrands(query),
      ]);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchProducts = async (query: string) => {
    try {
      console.log('🔍 Searching for products with query:', query);
      
      // Try API search first
      let results: Product[] = [];
      try {
        results = await apiService.getProducts({ search: query, limit: 100 });
        console.log('🔍 API search results received:', {
          query: query,
          resultCount: results.length,
          results: results.slice(0, 5).map(p => p.name)
        });
      } catch (apiError) {
        console.warn('⚠️ API search failed, trying fallback:', apiError);
        // Fallback: Get all products and filter client-side
        try {
          const allProducts = await apiService.getProducts({ limit: 1000 });
          console.log('🔍 Fallback: Loaded all products for client-side filtering:', allProducts.length);
          results = allProducts;
        } catch (fallbackError) {
          console.error('❌ Fallback also failed:', fallbackError);
          setProductResults([]);
          return;
        }
      }
      
      // Always filter client-side to ensure results match (API might not filter correctly)
      const queryLower = query.toLowerCase().trim();
      const matchingResults = results.filter(product => {
        const nameMatch = product.name.toLowerCase().includes(queryLower);
        const brandMatch = product.brand && product.brand.toLowerCase().includes(queryLower);
        const categoryMatch = product.category && product.category.toLowerCase().includes(queryLower);
        const descriptionMatch = product.description && product.description.toLowerCase().includes(queryLower);
        return nameMatch || brandMatch || categoryMatch || descriptionMatch;
      });
      
      console.log('🔍 Filtered results:', {
        query: query,
        totalFromAPI: results.length,
        afterFiltering: matchingResults.length,
        matchingProducts: matchingResults.slice(0, 10).map(p => p.name)
      });
      
      // Limit to 20 results
      setProductResults(matchingResults.slice(0, 20));
    } catch (error) {
      console.error('❌ Product search error:', error);
      setProductResults([]);
    }
  };

  const searchShops = async (query: string) => {
    try {
      const shops = await shopService.getShops();
      const queryLower = query.toLowerCase();
      const filtered = shops.filter(shop =>
        shop.name.toLowerCase().includes(queryLower) ||
        shop.type?.toLowerCase().includes(queryLower) ||
        shop.description?.toLowerCase().includes(queryLower)
      );
      setShopResults(filtered.slice(0, 20));
    } catch (error) {
      console.error('Shop search error:', error);
      setShopResults([]);
    }
  };

  const searchCategories = async (query: string) => {
    try {
      const categories = await apiService.searchCategories(query, 20);
      setCategoryResults(categories);
    } catch (error) {
      console.error('Category search error:', error);
      setCategoryResults([]);
    }
  };

  const searchBrands = async (query: string) => {
    try {
      const brands = await apiService.searchBrands(query, 20);
      setBrandResults(brands.map(b => ({ name: b.name, productCount: b.productCount || 0 })));
    } catch (error) {
      console.error('Brand search error:', error);
      setBrandResults([]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    handleSearch(query);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.length >= 2) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`, { replace: true });
      handleSearch(searchQuery);
    }
  };

  const getResultsCount = () => {
    switch (activeTab) {
      case 'products':
        return productResults.length;
      case 'shops':
        return shopResults.length;
      case 'categories':
        return categoryResults.length;
      case 'brands':
        return brandResults.length;
      default:
        return 0;
    }
  };

  const handleProductClick = (product: Product) => {
    setSelectedProductForCart(product);
    setShowShopModal(true);
  };

  const handleShopSelect = (shop: Shop, product: Product) => {
    // Add product to cart with selected shop
    try {
      // Get existing cart from localStorage
      const existingCart = localStorage.getItem('multiShopCart');
      let cart: Array<{ shop: Shop; items: Array<{ product: ShopProduct; quantity: number }> }> = [];
      
      if (existingCart) {
        try {
          cart = JSON.parse(existingCart);
        } catch (e) {
          console.error('Error parsing cart:', e);
        }
      }

      // Convert product to ShopProduct format
      const shopProduct: ShopProduct = {
        ...product as any,
        id: String(product.id),
        shopId: shop.id,
        shop_id: shop.id,
        category_id: product.category_id,
        stock: (product as any).stock_quantity || 0,
        inStock: product.inStock !== undefined ? product.inStock : true,
        createdAt: product.created_at || new Date().toISOString(),
        updatedAt: product.updated_at || new Date().toISOString()
      };

      // Find or create shop entry in cart
      let shopCart = cart.find(c => c.shop.id === shop.id);
      
      if (!shopCart) {
        shopCart = { shop, items: [] };
        cart.push(shopCart);
      }

      // Check if product already exists in this shop's cart
      const existingItem = shopCart.items.find(item => item.product.id === String(product.id));
      
      if (existingItem) {
        existingItem.quantity += 1;
      } else {
        shopCart.items.push({ product: shopProduct, quantity: 1 });
      }

      // Save to localStorage
      try {
        localStorage.setItem('multiShopCart', JSON.stringify(cart));
        console.log('🛒 SearchPage: Cart saved to localStorage');
        
        // Dispatch cart update event immediately, then again with delay as fallback
        const event = new CustomEvent('cartUpdated');
        window.dispatchEvent(event);
        console.log('🛒 SearchPage: cartUpdated event dispatched immediately');
        // Also dispatch with small delay as fallback
        setTimeout(() => {
          window.dispatchEvent(event);
          console.log('🛒 SearchPage: cartUpdated event dispatched (fallback)');
        }, 10);
      } catch (error) {
        console.error('Error saving cart:', error);
        throw error;
      }

      console.log('✅ Product added to cart:', {
        product: product.name,
        shop: shop.name,
        cartItems: cart.reduce((sum, sc) => sum + sc.items.length, 0)
      });

      // Close modal after adding to cart
      setShowShopModal(false);
      setSelectedProductForCart(null);
    } catch (error) {
      console.error('Error adding product to cart:', error);
    }
  };

  // Wishlist state
  const [wishlist, setWishlist] = useState<any[]>(() => {
    try {
      const saved = localStorage.getItem('wishlist');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Wishlist functions
  const isInWishlist = (productId: string): boolean => {
    return wishlist.some(item => String(item.id) === String(productId));
  };

  const handleAddToWishlist = (product: Product, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (isInWishlist(String(product.id))) {
      // Remove from wishlist
      const updated = wishlist.filter(item => String(item.id) !== String(product.id));
      setWishlist(updated);
      localStorage.setItem('wishlist', JSON.stringify(updated));
      // Dispatch event to update wishlist count in header
      window.dispatchEvent(new CustomEvent('wishlistUpdated'));
    } else {
      // Add to wishlist
      const wishlistItem = {
        id: product.id,
        name: product.name,
        price: product.price,
        originalPrice: (product as any).originalPrice || (product as any).original_price,
        discount: product.discount || 0,
        image: product.image || '📦',
        rating: (product as any).rating || 0,
        brand: product.brand || '',
        weight: (product as any).weight || '',
        category: product.category || ''
      };
      const updated = [...wishlist, wishlistItem];
      setWishlist(updated);
      localStorage.setItem('wishlist', JSON.stringify(updated));
      // Dispatch event to update wishlist count in header
      window.dispatchEvent(new CustomEvent('wishlistUpdated'));
    }
  };

  const renderProductItem = (product: Product) => (
    <div
      key={product.id}
      className="result-card"
      onClick={() => handleProductClick(product)}
    >
      <div className="result-image">
        {product.image && !product.image.startsWith('📦') && !product.image.startsWith('🛍️') ? (
          <img src={getValidImageUrl(product.image)} alt={product.name} />
        ) : (
          <span className="result-emoji">📦</span>
        )}
        <button
          className={`wishlist-btn ${isInWishlist(String(product.id)) ? 'active' : ''}`}
          onClick={(e) => handleAddToWishlist(product, e)}
          aria-label={isInWishlist(String(product.id)) ? 'Remove from wishlist' : 'Add to wishlist'}
          title={isInWishlist(String(product.id)) ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          {isInWishlist(String(product.id)) ? '❤️' : '🤍'}
        </button>
      </div>
      <div className="result-info">
        <div className="result-name">{product.name}</div>
        {product.brand && <div className="result-subtext">Brand: {product.brand}</div>}
        <div className="result-category">{product.category}</div>
        <div className="result-price">₹{product.price}</div>
      </div>
      <span className="result-arrow">→</span>
    </div>
  );

  const renderShopItem = (shop: Shop) => {
    const handleShopClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      console.log('🔍 SearchPage: Clicking shop:', shop.id, shop.name);
      console.log('🔍 SearchPage: Navigating to shop page:', `/shop/${shop.id}`);
      // Navigate to dedicated shop page (matching mobile app)
      navigate(`/shop/${shop.id}`);
    };

    return (
      <div
        key={shop.id}
        className="result-card"
        onClick={handleShopClick}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleShopClick(e as any);
          }
        }}
      >
      <div className="result-image">
        {shop.logo ? (
          <img src={getValidImageUrl(shop.logo)} alt={shop.name} />
        ) : (
          <span className="result-emoji">🏪</span>
        )}
      </div>
      <div className="result-info">
        <div className="result-name">{shop.name}</div>
        {shop.type && <div className="result-subtext">{shop.type}</div>}
        {shop.description && <div className="result-category">{shop.description}</div>}
        <div className="rating-row">
          <span className="rating-star">⭐</span>
          <span className="rating-text">4.5</span>
          <span className="reviews-text">(10 reviews)</span>
        </div>
      </div>
      <span className="result-arrow">→</span>
    </div>
    );
  };

  const renderCategoryItem = (category: Category) => (
    <div
      key={category.id}
      className="result-card"
      onClick={() => navigate('/browse-all', {
        state: {
          categoryId: category.id,
          categoryName: translateCategoryName(category.name),
          filterType: 'category'
        }
      })}
    >
      <div className="category-icon-container">
        <span className="category-icon">{category.icon || '📦'}</span>
      </div>
      <div className="result-info">
        <div className="result-name">{translateCategoryName(category.display_name || category.name)}</div>
        {category.description && <div className="result-category">{category.description}</div>}
      </div>
      <span className="result-arrow">→</span>
    </div>
  );

  const renderBrandItem = (brand: BrandResult) => (
    <div
      key={brand.name}
      className="result-card"
      onClick={() => {
        setSearchQuery(brand.name);
        setActiveTab('products');
        handleSearch(brand.name);
      }}
    >
      <div className="brand-icon-container">
        <span className="brand-icon">🏷️</span>
      </div>
      <div className="result-info">
        <div className="result-name">{brand.name}</div>
        {brand.productCount !== undefined && (
          <div className="result-subtext">{brand.productCount} products</div>
        )}
      </div>
      <span className="result-arrow">→</span>
    </div>
  );

  const renderTabButton = (tab: SearchTab, label: string, icon: string) => {
    const isActive = activeTab === tab;
    const count = getResultsCount();
    
    return (
      <button
        key={tab}
        className={`tab-button ${isActive ? 'active' : ''}`}
        onClick={() => setActiveTab(tab)}
      >
        <span className="tab-icon">{icon}</span>
        <span className="tab-label">{label}</span>
        {count > 0 && (
          <span className={`tab-badge ${isActive ? 'active' : ''}`}>{count}</span>
        )}
      </button>
    );
  };

  const renderResults = () => {
    if (loading) {
      return (
        <div className="center-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Searching...</p>
        </div>
      );
    }

    if (searchQuery.length < 2) {
      return (
        <div className="center-container">
          <div className="empty-icon">🔍</div>
          <p className="empty-text">Search for products, shops, categories, or brands</p>
        </div>
      );
    }

    let results: any[] = [];
    switch (activeTab) {
      case 'products':
        results = productResults;
        break;
      case 'shops':
        results = shopResults;
        break;
      case 'categories':
        results = categoryResults;
        break;
      case 'brands':
        results = brandResults;
        break;
    }

    if (results.length === 0) {
      return (
        <div className="center-container">
          <div className="empty-icon">😔</div>
          <p className="empty-text">No {activeTab} found</p>
          <p className="empty-subtext">Try a different search term</p>
        </div>
      );
    }

    return (
      <div className="results-list">
        {activeTab === 'products' && productResults.map(product => renderProductItem(product))}
        {activeTab === 'shops' && shopResults.map(shop => renderShopItem(shop))}
        {activeTab === 'categories' && categoryResults.map(category => renderCategoryItem(category))}
        {activeTab === 'brands' && brandResults.map(brand => renderBrandItem(brand))}
      </div>
    );
  };

  return (
    <div className="search-page">
      {/* Header - Using CustomerHeader component for consistency */}
      <CustomerHeader
        variant="sticky"
        showTagline
        showVoiceSearch
        showProfileDropdown
        showBackButton={true}
        showLoyaltyButton
        cartCount={cartCount}
        onBackClick={() => navigate('/app')}
        onSearch={(query) => {
          setSearchQuery(query);
          handleSearch(query);
        }}
        onCartClick={() => navigate('/app', { state: { showCart: true } })}
      />

      {/* Search Bar */}
      <div className="search-page-search">
        <form onSubmit={handleSubmit} className="search-form">
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              ref={searchInputRef}
              type="text"
              className="search-input"
              placeholder="Search products, shops, brands..."
              value={searchQuery}
              onChange={handleInputChange}
              aria-label="Search products, shops, brands"
            />
            {searchQuery.length > 0 && (
              <button
                type="button"
                className="clear-button"
                onClick={() => {
                  setSearchQuery('');
                  setProductResults([]);
                  setShopResults([]);
                  setCategoryResults([]);
                  setBrandResults([]);
                }}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Search Tabs */}
      <div className="tabs-container">
        <div className="tabs-content">
          {renderTabButton('products', 'Products', '📦')}
          {renderTabButton('shops', 'Shops', '🏪')}
          {renderTabButton('categories', 'Categories', '📋')}
          {renderTabButton('brands', 'Brands', '🏷️')}
        </div>
      </div>

      {/* Results */}
      <div className="search-page-content">
        {renderResults()}
      </div>

      {/* Shop Selection Modal */}
      <ShopSelectionModal
        isOpen={showShopModal}
        product={selectedProductForCart}
        onClose={() => {
          setShowShopModal(false);
          setSelectedProductForCart(null);
        }}
        onSelectShop={handleShopSelect}
      />
    </div>
  );
};

export default SearchPage;
